"use client";

import Link from "next/link";
import { ROUTES } from "@lib/routes";
import { useCallback, useEffect, useRef, useState } from "react";
import { InsightCategoryBadge } from "@components/tutor-memory/InsightCategoryBadge";
import { useTranslations } from "@i18n/provider";
import { useLessonPreferences } from "@components/lesson/LessonPreferencesProvider";

type BriefItem = {
  id: string;
  category: string;
  text: string;
  sourceType: string;
};

type BriefResponse = {
  brief: string;
  items: BriefItem[];
};

type TutorMemoryPanelProps = {
  open: boolean;
  onClose?: () => void;
  variant?: "header" | "debrief";
};

export function TutorMemoryPanel({ open, variant = "header" }: TutorMemoryPanelProps) {
  const { t } = useTranslations();
  const { reducedMotion } = useLessonPreferences();
  const [data, setData] = useState<BriefResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const fetchedRef = useRef(false);

  const loadBrief = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await fetch("/api/tutor-memory/brief");
      if (!res.ok) {
        setError(true);
        return;
      }
      const json = (await res.json()) as BriefResponse;
      setData(json);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || fetchedRef.current) return;
    fetchedRef.current = true;
    void loadBrief();
  }, [open, loadBrief]);

  if (!open) return null;

  const isDebrief = variant === "debrief";
  const textClass = isDebrief ? "text-ink-body" : "text-white/90";
  const eyebrowClass = isDebrief ? "text-ink-faint" : "text-white/70";
  const linkClass = isDebrief ? "text-reading-fg hover:underline" : "text-white/90 hover:underline";
  const panelClass = isDebrief
    ? "border-b border-ink/10 bg-surface-sunk"
    : "border-b border-white/10";

  const hasContent = Boolean(data?.brief || (data?.items?.length ?? 0) > 0);

  return (
    <div
      id="tutor-memory-panel"
      className={`max-h-[200px] overflow-y-auto px-3 py-3 ${panelClass} ${reducedMotion ? "" : "animate-pop-in"}`}
    >
      {loading ? (
        <div className="flex flex-col gap-2">
          <div className="shimmer-bar w-full rounded" />
          <div className="shimmer-bar w-3/4 rounded" />
        </div>
      ) : error ? (
        <div className="flex flex-col gap-2">
          <p className={`text-sm italic ${textClass}`}>{t("agent.tutorMemory.panelError")}</p>
          <button
            type="button"
            onClick={() => void loadBrief()}
            className={`min-h-[44px] text-left text-sm font-semibold underline ${linkClass}`}
          >
            {t("common.retry")}
          </button>
        </div>
      ) : !hasContent ? (
        <p className={`text-sm italic ${textClass}`}>{t("agent.tutorMemory.panelEmpty")}</p>
      ) : (
        <>
          {data?.brief ? (
            <section className="mb-3">
              <p className={`eyebrow ${eyebrowClass}`}>
                {t("agent.tutorMemory.panelBriefEyebrow")}
              </p>
              <p className={`mt-1 text-sm leading-relaxed ${textClass}`}>{data.brief}</p>
            </section>
          ) : null}

          {(data?.items?.length ?? 0) > 0 ? (
            <section className="mb-3">
              <p className={`eyebrow ${eyebrowClass}`}>{t("agent.tutorMemory.panelTopEyebrow")}</p>
              <ul className="mt-2 flex flex-col">
                {data!.items.slice(0, 3).map((item) => (
                  <li key={item.id} className="mb-3 flex flex-col gap-1 last:mb-0">
                    <InsightCategoryBadge category={item.category} sourceType={item.sourceType} />
                    <p className={`line-clamp-2 text-sm ${textClass}`}>{item.text}</p>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <Link
            href={ROUTES.tutorMemory}
            className={`flex min-h-[44px] items-center text-sm ${linkClass}`}
          >
            {t("agent.tutorMemory.viewAll")}
          </Link>
        </>
      )}
    </div>
  );
}
